<template>
  <table class="full-width-table reminders-table">
    <tbody>
      <tr class="table-header">
        <th style="width: 10%">Date</th>
        <th style="width: 10%">Type</th>
        <th style="width: auto">Reminder</th>
        <th style="width: 15%">Assigned To</th>
        <th style="width: 10%">Complete</th>
        <th style="width: 6rem"></th>
      </tr>
      <tr v-if="!loading && reminderEntries.length > 0" v-for="entry in reminderEntries" :key="entry.REMINDERID" @click="editReminderEntry(entry)" class="reminder-entry-row">
        <td>
          <span>{{ formatDate(entry.DATE) }}</span>
        </td>
        <td>
          <span>{{ entry.TYPE }}</span>
        </td>
        <td class="reminder-cell">
          <span class="reminder-text">{{ entry.TEXT }}</span>
        </td>
        <td>
          <span>{{ getDisplayName(entry.ASSIGNEDID) }}</span>
        </td>
        <td>
          <span>{{ entry.COMPLETED ? 'Yes' : 'No' }}</span>
        </td>
        <td>
          <div class="actions-container" @click.stop>
            <div class="icon-button small-icon" @click="confirmDeleteEntry(entry)" title="Delete" v-if="user.contactsAdmin || entry.ASSIGNEDID === user.userid">
              <img id="noHover" src="../assets/trash-bubble.svg" alt="Delete" />
              <img id="hover" src="../assets/trash-bubble-hover.svg" alt="Delete" />
            </div>
          </div>
        </td>
      </tr>
      <tr v-if="!loading && reminderEntries.length === 0" class="no-results">
        <td colspan="6">No reminders found</td>
      </tr>
      <tr v-if="loading" class="loading-row">
        <td colspan="6"><img src="../assets/spinner.svg" /></td>
      </tr>
    </tbody>
  </table>

  <!-- Edit Reminder Popup -->
  <EditReminder v-if="showEditReminder" :reminderEntry="selectedEntry" :entityType="type" :entityId="entityid" @closePopup="showEditReminder = false" @save="handleReminderSave" />

  <!-- Delete Confirmation -->
  <Confirmation
    v-if="showDeleteConfirm"
    @confirm="handleDeleteEntry"
    @cancel="showDeleteConfirm = false"
    header="Delete Reminder"
    message="This will permanently delete this reminder. Are you sure?"
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
</template>

<script>
import { ref, computed, watch } from 'vue';
import useAlerts from '@/composables/useAlerts';
import useReminderQuery from '@/composables/useReminderQuery';
import useReminderMutate from '@/composables/useReminderMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUsersQuery from '@/composables/useUsersQuery';
import Confirmation from './Confirmation.vue';
import EditReminder from './EditReminder.vue';
import useCurrentUser from '../composables/useCurrentUser';

export default {
  name: 'RemindersTab',
  components: {
    Confirmation,
    EditReminder,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { addAlert } = useAlerts();
    const { getEntityReminders } = useReminderQuery;
    const queryReminders = getEntityReminders();
    const { deleteReminder } = useReminderMutate();
    const { decryptSymbols } = useDecryptSymbols();
    const { allUsers, getUsers } = useUsersQuery();
    const { user } = useCurrentUser();

    getUsers();

    // Reactive refs
    const reminderEntries = ref([]);
    const showDeleteConfirm = ref(false);
    const showEditReminder = ref(false);
    const selectedEntry = ref(null);
    const reminderQuery = ref(null);

    // Computed properties
    const loading = computed(() => {
      return reminderQuery.value?.loading || false;
    });

    const error = computed(() => {
      return reminderQuery.value?.error || null;
    });

    // Execute query function
    const executeQuery = () => {
      if (!props.entityid || props.entityid === 'new') {
        reminderQuery.value = null;
        reminderEntries.value = [];
        return;
      }

      // Set up query parameters based on type
      const queryParams = {};
      if (props.type === 'company') {
        queryParams.companyid = props.entityid;
      } else if (props.type === 'contact') {
        queryParams.contactid = props.entityid;
      }

      reminderQuery.value = queryReminders(queryParams);
    };

    // Watch for props changes
    watch(() => [props.type, props.entityid], executeQuery, { immediate: true });

    // Watch for query result changes and update reminderEntries
    watch(
      () => reminderQuery.value?.result?.entityReminders,
      (newResult) => {
        if (newResult) {
          const decryptedEntries = decryptSymbols(newResult);
          // Sort by date, earliest first
          reminderEntries.value = decryptedEntries.sort((a, b) => {
            // Try to parse as integer timestamp first
            const timestampA = parseInt(a.DATE);
            const timestampB = parseInt(b.DATE);

            if (!isNaN(timestampA) && !isNaN(timestampB)) {
              return timestampA - timestampB; // Ascending order (earliest first)
            }

            // Fall back to date parsing
            const dateA = new Date(a.DATE);
            const dateB = new Date(b.DATE);
            return dateA - dateB; // Ascending order (earliest first)
          });
        } else {
          reminderEntries.value = [];
        }
      },
      { immediate: true, deep: true }
    );

    return {
      loading,
      error,
      reminderEntries,
      showDeleteConfirm,
      showEditReminder,
      selectedEntry,
      addAlert,
      executeQuery,
      allUsers,
      deleteReminder,
      user,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      try {
        // Try to parse as integer timestamp first
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          return date.toLocaleDateString('en-US');
        }

        // Fall back to string parsing
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US');
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    },
    getDisplayName(assignedId) {
      if (!assignedId) return 'Unknown';

      const user = this.allUsers.find((user) => user.USERID === assignedId);
      return user?.DISPLAYNAME || 'Unknown';
    },
    editReminderEntry(entry) {
      this.selectedEntry = entry;
      this.showEditReminder = true;
    },
    confirmDeleteEntry(entry) {
      this.selectedEntry = entry;
      this.showDeleteConfirm = true;
    },
    async handleDeleteEntry() {
      this.showDeleteConfirm = false;

      if (!this.selectedEntry) return;

      try {
        await this.deleteReminder(this.selectedEntry.REMINDERID);
        // Refresh the reminder entries after successful deletion
        this.executeQuery();
      } catch (error) {
        // Error handling is done in the composable
      } finally {
        this.selectedEntry = null;
      }
    },
    handleReminderSave() {
      this.showEditReminder = false;
      this.selectedEntry = null;
      // Refresh the reminder entries after successful save
      this.executeQuery();
    },
    openAddReminder() {
      this.selectedEntry = null;
      this.showEditReminder = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.reminders-table {
  th:first-child,
  td:first-child {
    padding-left: 3.75rem;
  }

  td {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .loading-row {
    td {
      background: white;
    }
  }
}

.reminder-cell {
  .reminder-text {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-right: 1rem;
}
</style>
